import { useState } from "react";
import Reveal from "@/components/feature/Reveal";
import { clinic } from "@/mocks/clinic";
import { ReserveButton } from "@/components/layout/ReserveButton";
import { LineButton } from "@/components/layout/LineButton";
import { isPendingText } from "@/utils/isPendingContent";

/** FAQ */
export function TreatmentFaq({ items }: { items: { q: string; a: string }[] }) {
  // 回答が未確定（「確認中」「確定次第掲載」等）のFAQは患者向けに表示しない
  const faqs = items.filter((faq) => !isPendingText(faq.a) && !isPendingText(faq.q));
  const [openId, setOpenId] = useState<number | null>(0);

  if (faqs.length === 0) return null;

  return (
    <div className="border-t border-background-300/80">
      {faqs.map((faq, index) => {
        const isOpen = openId === index;
        return (
          <div key={faq.q} className="border-b border-background-300/80">
            <button
              type="button"
              onClick={() => setOpenId(isOpen ? null : index)}
              aria-expanded={isOpen}
              className="flex w-full items-center justify-between gap-6 py-6 text-left"
            >
              <span className="flex items-start gap-4">
                <span className="font-label text-[13px] text-secondary-700">Q</span>
                <span className="font-heading text-[15px] leading-[1.8] text-foreground-950 md:text-base">
                  {faq.q}
                </span>
              </span>
              <i
                className={`shrink-0 text-xl text-foreground-600 transition-transform duration-300 ${
                  isOpen ? "rotate-45" : ""
                }`}
                aria-hidden
              >
                <span className="ri-add-line" />
              </i>
            </button>
            <div
              className={`grid transition-all duration-300 ease-out ${
                isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
              }`}
            >
              <div className="overflow-hidden">
                <div className="flex items-start gap-4 pb-7">
                  <span className="font-label text-[13px] text-accent-700">A</span>
                  <p className="text-[13px] leading-[2.1] text-foreground-700 md:text-sm">{faq.a}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 施術ページ下部の共通CTA（LINE相談 / WEB予約） */
export function TreatmentCta({ lineLabel, treatmentJp }: { lineLabel: string; treatmentJp: string }) {
  return (
    <section className="relative overflow-hidden bg-primary-700 py-20 md:py-24 lg:py-28">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 top-1/2 h-72 w-72 -translate-y-1/2 rounded-full border border-background-50/10"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-40 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full border border-background-50/10"
      />
      <div className="u-container relative">
        <Reveal>
          <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
            <span className="en-eyebrow text-[10px] text-accent-300 md:text-[11px]">CONTACT / RESERVATION</span>
            <h2 className="mt-7 font-heading text-[24px] leading-[1.6] text-background-50 md:text-[34px]">
              {treatmentJp}のご相談は、
              <br />
              お気軽にどうぞ。
            </h2>
            <p className="mt-7 max-w-xl text-[13px] leading-[2.2] text-background-200 md:text-sm">
              施術内容が決まっていない方も、まずはお気軽にご相談ください。一人ひとりのお悩みに合わせて、納得できる美容医療をご提案します。
            </p>

            <div className="mt-10 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center">
              <LineButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-8 py-3.5 text-[14px] font-medium text-primary-700 transition-colors hover:bg-secondary-100">
                <i className="ri-line-fill text-base" aria-hidden />
                {lineLabel}
              </LineButton>
              <ReserveButton className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-8 py-3.5 text-[14px] text-background-50 transition-colors hover:bg-background-50/10">
                <i className="ri-calendar-check-line text-base" aria-hidden />
                無料カウンセリングを予約
              </ReserveButton>
              <a
                href={clinic.telHref}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md px-8 py-3.5 text-[14px] text-background-200 transition-colors hover:text-background-50"
              >
                <i className="ri-phone-line text-base" aria-hidden />
                電話する
              </a>
            </div>

            <p className="mt-8 text-[12px] tracking-wide text-background-200/80">
              {clinic.hours} ／ 渋谷駅近く
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}